import React, { useState, useCallback } from "react";
import { bucketTick, bucketLabel, isWeekendISO } from "./dateUtils";
import { useTranslation } from "../../hooks/useTranslation";

/* Gráfico de barras EMPILHADAS em divs (sem lib), transposto do mockup:
   - cada coluna é um bucket (dia ou hora); segmentos = grupos do "Agrupar por"
   - colunas vazias aparecem com base cinza (dias sem consumo)
   - tooltip custom (position:fixed) com a quebra por grupo + total
   - clique numa coluna (granularity=day) dispara o drill-down hora a hora */
const StackedBarChart = ({
  buckets = [],
  groups = [], // [{key, label, color}]
  matrix = [], // [bucketIdx][groupIdx]
  granularity = "day",
  formatter = (v) => String(v),
  metricLabel = "",
  onBarClick = null,
  selectedBucket = null,
}) => {
  const { t } = useTranslation("consumption");
  const [tip, setTip] = useState(null); // {x, y, bucketIdx}

  const totalsPerBucket = buckets.map((_, b) =>
    (matrix[b] || []).reduce((a, v) => a + (v || 0), 0)
  );
  const maxValue = Math.max(1, ...totalsPerBucket);
  // Rótulos rotacionados a 90°: cabem ~45 sem sobrepor. Para períodos longos,
  // pula rótulos mantendo no máximo ~45 visíveis (sempre o primeiro).
  const tickEvery = Math.max(1, Math.ceil(buckets.length / 45));
  const clickable = granularity === "day" && typeof onBarClick === "function";
  // Sem nenhum consumo no período → mostra mensagem clara em vez de um quadro
  // vazio (que parecia o gráfico "quebrado").
  const hasData = totalsPerBucket.some((tt) => tt > 0);

  const handleMove = useCallback((e, bucketIdx) => {
    const x = Math.min(e.clientX + 14, window.innerWidth - 250);
    const y = Math.min(e.clientY + 14, window.innerHeight - 180);
    setTip({ x, y, bucketIdx });
  }, []);

  const handleLeave = useCallback(() => setTip(null), []);

  if (!hasData) {
    return (
      <div className="chart-wrap">
        <div className="chart-empty">{t("noData")}</div>
      </div>
    );
  }

  return (
    <div className="chart-wrap">
      <div className="chart-ymax">{formatter(maxValue)}</div>
      {/* faixa rolável: barras mantêm largura mínima e rolam na horizontal
          quando há muitos buckets (período longo) em vez de virar sub-pixel */}
      <div className="chart-scroll">
        <div className="chart">
          <div className="grid" style={{ bottom: "33%" }} />
        <div className="grid" style={{ bottom: "66%" }} />
        <div className="grid" style={{ bottom: "99%" }} />
        {buckets.map((bucket, b) => {
          const total = totalsPerBucket[b];
          const heightPct = total > 0 ? (total / maxValue) * 100 : 0;
          const empty = total === 0;
          return (
            <div
              key={bucket}
              className={`bar-col ${empty ? "empty" : ""} ${
                selectedBucket === bucket ? "sel" : ""
              } ${clickable && !empty ? "clickable" : ""}`}
              onMouseMove={(e) => handleMove(e, b)}
              onMouseLeave={handleLeave}
              onClick={() => {
                if (clickable && !empty) onBarClick(bucket);
              }}
            >
              <div
                className="bar-stack"
                style={{ height: `${heightPct}%` }}
              >
                {groups.map((g, gi) => {
                  const v = (matrix[b] || [])[gi] || 0;
                  if (!v || total === 0) return null;
                  return (
                    <div
                      key={g.key}
                      className="bar-seg"
                      style={{
                        height: `${(v / total) * 100}%`,
                        background: g.color,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="xaxis">
        {buckets.map((bucket, b) => (
          <div
            key={bucket}
            className={`xtick ${
              granularity === "day" && isWeekendISO(bucket) ? "wk" : ""
            }`}
          >
            {b % tickEvery === 0 ? (
              <span>{bucketTick(bucket, granularity)}</span>
            ) : null}
          </div>
        ))}
        </div>
      </div>

      {tip && (
        <div
          className="chart-tip show"
          style={{ left: tip.x, top: tip.y }}
        >
          <div className="t-date">
            {bucketLabel(buckets[tip.bucketIdx], granularity)}
          </div>
          {totalsPerBucket[tip.bucketIdx] === 0 ? (
            <div className="t-row">
              <span className="l">{t("noConsumption")}</span>
            </div>
          ) : (
            groups.map((g, gi) => {
              const v = (matrix[tip.bucketIdx] || [])[gi] || 0;
              if (!v) return null;
              return (
                <div className="t-row" key={g.key}>
                  <span className="l">
                    <span className="sw" style={{ background: g.color }} />
                    {g.label}
                  </span>
                  <span className="v">{formatter(v)}</span>
                </div>
              );
            })
          )}
          <div className="t-row t-total">
            <span className="l">
              {t("total")} {metricLabel}
            </span>
            <span className="v">
              {formatter(totalsPerBucket[tip.bucketIdx])}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
